// components/PaginatedTable.jsx
import React, { useMemo } from "react";
import Pagination from "./Pagination";

const cx = (...c) => c.filter(Boolean).join(" ");

export default function PaginatedTable({
  rows,
  loading,
  error,
  meta,
  onChange,
  pageSizeOptions = [25, 40, 60, 100],
  // columns: [{ key, header, render? }]
  columns,
  actions,        // (row) => ReactNode
  emptyText = "No data",
}) {
  const headers = useMemo(() => columns.map(c => c.header ?? String(c.key)), [columns]);

  return (
    <section className="rounded-xl border border-slate-800/80 overflow-auto">
      {error && (
        <div className="m-3 rounded-lg border border-red-800/50 bg-red-900/35 text-red-100 px-3 py-2">
          {error}
        </div>
      )}
      <table className="min-w-full table-auto text-sm">
        <colgroup>
          {columns.map((c) => <col key={c.key} />)}
          {actions && <col className="w-48" />}
        </colgroup>
        <thead className="bg-slate-900/80 sticky top-0 z-10 border-b border-slate-800/80">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="text-left uppercase tracking-wide text-[11px] text-slate-100 px-3 py-2">
                {h}
              </th>
            ))}
            {actions && (
              <th className="w-48 text-left uppercase tracking-wide text-[11px] text-slate-100 px-3 py-2">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/80">
          {loading && rows.length === 0 ? (
            [...Array(5)].map((_, i) => (
              <tr key={i} className="animate-pulse">
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-3">
                    <div className="h-3 rounded bg-slate-700/50" />
                  </td>
                ))}
                {actions && (
                  <td className="px-3 py-3">
                    <div className="h-8 w-40 rounded bg-slate-700/50 ml-auto" />
                  </td>
                )}
              </tr>
            ))
          ) : rows.length > 0 ? (
            rows.map((row) => (
              <tr key={row.id ?? row.code}>
                {columns.map((c) => (
                  <td key={c.key} className={cx("px-3 py-3 align-middle", c.className)}>
                    {c.render ? c.render(row[c.key], row) : String(row[c.key] ?? "—")}
                  </td>
                ))}
                {actions && (
                  <td className="px-3 py-2 align-middle text-left w-48 shrink-0 whitespace-nowrap">
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={(columns?.length ?? 0) + (actions ? 1 : 0)} className="py-12 text-center text-slate-400">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {rows.length > 0 && (
        <div className="p-3">
          <Pagination meta={meta} onChange={onChange} pageSizeOptions={pageSizeOptions} />
        </div>
      )}
    </section>
  );
}
